import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import isEqual from 'lodash/isEqual';
import { type Ref, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import {
  type ChannelMappingCustomParameterType,
  type ChannelMappingRuleDataType,
} from '@/api/channelMappingRules';
import { type OptionType } from '@/api/common';
import DeleteIcon from '@/assets/svg/trashcan.svg?react';
import { Button } from '@/components/buttons';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import { TextAreaAutosize } from '@/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import { type QueryStateFilterMulti, useInstantSearchState } from '@/components/InstantSearch';
import { maxObjectDescriptionLength } from '@/constants/numbers';
import type SolAPIError from '@/error/SolAPIError';
import { ChannelSelect } from '@/features/inputs';
import VendorSelect from '@/features/inputs/VendorSelect';
import { cn } from '@/lib/utils';

import ChannelMappingRuleDeleteDialog from './ChannelMappingRuleDeleteDialog';
import { NEW_ROW_ID_PREFIX } from './ChannelMappingRules';
import {
  displayCustomParamsAsText,
  displayCommaSeparatedValuesAsText as displayRuleAsCSV,
  parseCustomParameters,
  parseCommaSeparatedValues as parseRuleCSV,
} from './util';

const HIGHLIGHTED_ROW_REMOVAL_TIMER = 5000;

const ROW_SCROLL_DATA_ATTR = 'data-channel-mapping-rule-row';

type Props = {
  ref?: Ref<ChannelMappingRulesTableRef>;
  data: ChannelMappingRuleDataType[];
  invalidRowKeys?: string[];
  isLoading?: boolean;
  error: SolAPIError | null;
  isEditMode: boolean;
  onDataChange: (newData: ChannelMappingRuleDataType[]) => void;
  onValidate: (newData: ChannelMappingRuleDataType[]) => void;
};

export type ChannelMappingRulesTableRef = {
  scrollToRow: (key: string) => void;
  highlightRows: (keys: string[]) => void;
};

const updateDataPriority = (data: ChannelMappingRuleDataType[]) =>
  data.map((item, i) => ({ ...item, priority: i }));

const scrollRowIntoView = (host: HTMLElement | null, rowId: string) => {
  if (host == null) {
    return;
  }
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(rowId) : rowId;
  const el = host.querySelector<HTMLElement>(`[${ROW_SCROLL_DATA_ATTR}="${escaped}"]`);
  el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
};

const ChannelMappingRulesTable = ({
  ref,
  data,
  invalidRowKeys = [],
  isLoading,
  error,
  isEditMode,
  onDataChange,
  onValidate,
}: Props) => {
  const tableHostRef = useRef<HTMLDivElement>(null);
  const { queryState } = useInstantSearchState();
  const [highlightedRowKeys, setHighlightedRowKeys] = useState<string[]>([]);
  const [deleteRow, setDeleteRow] = useState<ChannelMappingRuleDataType>();

  const filteredData = useMemo(() => {
    let localData = data;

    if (queryState?.search) {
      const searchCompareStr = queryState.search.trim().toLocaleLowerCase();

      localData = localData.filter((row) => {
        const vendorName = row.vendor?.name?.toLocaleLowerCase();
        const channelName = row.vendor?.channel?.name.toLocaleLowerCase();

        return (
          (vendorName?.indexOf(searchCompareStr) ?? -1) >= 0 ||
          (channelName?.indexOf(searchCompareStr) ?? -1) >= 0 ||
          row.id.startsWith(NEW_ROW_ID_PREFIX)
        );
      });
    }

    if (queryState?.filters) {
      localData = localData.filter((row) =>
        queryState.filters.every((filter) => {
          const multiFilter = filter as QueryStateFilterMulti;
          const operand = multiFilter.operand;
          const operands = Array.isArray(operand) ? operand : [operand];

          return operands.some((operandVal) => {
            if (multiFilter.field === 'vendor.id' && multiFilter.operator === 'in') {
              return row.vendor?.id === operandVal;
            }
            if (multiFilter.field === 'channel.id' && multiFilter.operator === 'in') {
              return row.vendor?.channel?.id === operandVal;
            }
            return true;
          });
        }),
      );
    }

    return localData;
  }, [data, queryState]);

  const priorityOptions = useMemo(
    () => data.filter((d) => !d.isC99).map((d) => String(d.priority)),
    [data],
  );

  const addHighlightedRowKeys = useCallback((keys: string[]) => {
    setHighlightedRowKeys((existingKeys) => existingKeys.concat(keys));
    setTimeout(() => {
      setHighlightedRowKeys((existingKeys) => existingKeys.filter((k) => keys.indexOf(k) < 0));
    }, HIGHLIGHTED_ROW_REMOVAL_TIMER);
  }, []);

  const scrollToKey = useCallback((key: string) => {
    setTimeout(() => {
      scrollRowIntoView(tableHostRef.current, key);
      setTimeout(() => {
        scrollRowIntoView(tableHostRef.current, key);
      }, 250);
    }, 0);
  }, []);

  const scrollAndHighlightRow = useCallback(
    (key: string) => {
      addHighlightedRowKeys([key]);
      scrollToKey(key);
    },
    [addHighlightedRowKeys, scrollToKey],
  );

  useImperativeHandle(ref, () => ({
    scrollToRow: (key: string) => scrollToKey(key),
    highlightRows: (keys: string[]) => addHighlightedRowKeys(keys),
  }));

  const columns = useMemo(() => {
    const handlePriorityChange = (field: ChannelMappingRuleDataType, newIndex: number) => {
      const i = data.findIndex((d) => d.id === field.id);
      const newData = [...data];
      const changedItem = newData.splice(i, 1)[0];
      newData.splice(newIndex, 0, changedItem);
      onDataChange(updateDataPriority(newData));
      scrollAndHighlightRow(changedItem.id);
    };

    const getRowAriaInvalid = (field: ChannelMappingRuleDataType) =>
      invalidRowKeys.includes(field.id);

    const handleFieldChange = (newField: ChannelMappingRuleDataType) => {
      const newData = data.map((field) => (field.id === newField.id ? newField : field));
      onDataChange(newData);
      if (invalidRowKeys.indexOf(newField.id) >= 0) {
        onValidate(newData);
      }
    };

    const handleChannelChange = (field: ChannelMappingRuleDataType, channel: OptionType | null) => {
      if (channel) {
        const newVendor = {
          channel: { id: channel.id, name: channel.name ?? '' },
          id: undefined,
          name: undefined,
        };
        handleFieldChange({ ...field, vendor: newVendor });
      }
    };

    const handleVendorChange = (field: ChannelMappingRuleDataType, vendor: OptionType | null) => {
      if (vendor) {
        const newVendor = { channel: field.vendor?.channel, ...vendor };
        handleFieldChange({ ...field, vendor: newVendor });
      }
    };

    const handleRuleChange = (
      field: ChannelMappingRuleDataType,
      fieldName: 'media' | 'sources' | 'referralDomains' | 'customParameters',
      newValue: string[] | ChannelMappingCustomParameterType[],
    ) => {
      const oldValue = field[fieldName];
      if (!isEqual(newValue, oldValue)) {
        handleFieldChange({ ...field, [fieldName]: newValue });
      }
    };

    const cols: DataTableColumnDef<ChannelMappingRuleDataType>[] = [
      {
        id: 'priority',
        header: t`Priority`,
        size: 90,
        minSize: 90,
        maxSize: 90,
        flex: 0,
        enableSorting: false,
        meta: { align: 'center' },
        cell: ({ row }) => {
          const field = row.original;
          return field.isC99 ? (
            ''
          ) : isEditMode ? (
            <Select
              size="medium"
              value={String(field.priority)}
              itemToStringValue={(item) => item ?? ''}
              itemToStringLabel={(item) => (item == null ? '' : String(+item + 1))}
              aria-invalid={getRowAriaInvalid(field)}
              onValueChange={(priority: string | null) => {
                if (priority) {
                  handlePriorityChange(field, +priority);
                }
              }}
            >
              <SelectTrigger className="w-20" aria-invalid={getRowAriaInvalid(field)}>
                <SelectValue placeholder={t`Choose priority`} />
              </SelectTrigger>
              <SelectContent>
                {priorityOptions.map((item) => (
                  <SelectItem key={item} value={item}>
                    {+item + 1}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            field.priority + 1
          );
        },
      },
      {
        id: 'vendor.channel.id',
        header: t`Channel`,
        size: 185,
        minSize: 185,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <ChannelSelect
              className="w-full"
              size="medium"
              value={field?.vendor?.channel}
              aria-invalid={getRowAriaInvalid(field)}
              onValueChange={(option) => handleChannelChange(field, option)}
            />
          ) : (
            field?.vendor?.channel?.name
          );
        },
      },
      {
        id: 'vendor.id',
        header: t`Vendor`,
        size: 185,
        minSize: 185,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <VendorSelect
              className="w-full"
              size="medium"
              channelId={field?.vendor?.channel?.id}
              value={field?.vendor as OptionType}
              aria-invalid={getRowAriaInvalid(field)}
              disabled={!field?.vendor?.channel?.id}
              onValueChange={(vendor: OptionType | null) => handleVendorChange(field, vendor)}
            />
          ) : (
            field?.vendor?.name
          );
        },
      },
      {
        id: 'media',
        header: t`Medium`,
        size: 220,
        minSize: 220,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <TextAreaAutosize
              size="medium"
              minRows={1}
              maxRows={10}
              defaultValue={displayRuleAsCSV(field.media)}
              aria-invalid={getRowAriaInvalid(field)}
              maxLength={maxObjectDescriptionLength}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                handleRuleChange(field, 'media', parseRuleCSV(e.currentTarget.value))
              }
            />
          ) : (
            displayRuleAsCSV(field.media)
          );
        },
      },
      {
        id: 'sources',
        header: t`Source`,
        size: 200,
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <TextAreaAutosize
              size="medium"
              minRows={1}
              maxRows={10}
              defaultValue={displayRuleAsCSV(field.sources)}
              aria-invalid={getRowAriaInvalid(field)}
              maxLength={maxObjectDescriptionLength}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                handleRuleChange(field, 'sources', parseRuleCSV(e.currentTarget.value))
              }
            />
          ) : (
            displayRuleAsCSV(field.sources)
          );
        },
      },
      {
        id: 'referralDomains',
        header: t`Referral Domain`,
        size: 200,
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <TextAreaAutosize
              size="medium"
              minRows={1}
              maxRows={10}
              defaultValue={displayRuleAsCSV(field.referralDomains)}
              aria-invalid={getRowAriaInvalid(field)}
              maxLength={maxObjectDescriptionLength}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                handleRuleChange(field, 'referralDomains', parseRuleCSV(e.currentTarget.value))
              }
            />
          ) : (
            displayRuleAsCSV(field.referralDomains)
          );
        },
      },
      {
        id: 'params',
        header: t`Custom Parameters`,
        size: 164,
        minSize: 164,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return isEditMode && !field.isC99 ? (
            <TextAreaAutosize
              size="medium"
              minRows={1}
              maxRows={10}
              defaultValue={displayCustomParamsAsText(field.customParameters)}
              aria-invalid={getRowAriaInvalid(field)}
              maxLength={maxObjectDescriptionLength}
              onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) =>
                handleRuleChange(
                  field,
                  'customParameters',
                  parseCustomParameters(e.currentTarget.value),
                )
              }
            />
          ) : (
            displayCustomParamsAsText(field.customParameters)
          );
        },
      },
    ];

    if (isEditMode) {
      cols.push({
        id: 'deleteButton',
        header: '',
        size: 50,
        minSize: 50,
        maxSize: 50,
        flex: 0,
        enableSorting: false,
        meta: { align: 'center' },
        cell: ({ row }) => {
          const field = row.original;
          return (
            <Button
              aria-label={t`Remove rule`}
              variant="ghost"
              shape="square"
              danger
              size="icon"
              onClick={() => setDeleteRow(field)}
            >
              <DeleteIcon />
            </Button>
          );
        },
      });
    }

    return cols;
  }, [
    data,
    priorityOptions,
    invalidRowKeys,
    isEditMode,
    onDataChange,
    setDeleteRow,
    scrollAndHighlightRow,
    onValidate,
  ]);

  const handleDeleteRow = () => {
    if (data && deleteRow) {
      onDataChange(updateDataPriority(data.filter((d) => d.id !== deleteRow.id)));
      setDeleteRow(undefined);
    }
  };

  const rowClassForRecord = useCallback(
    (record: ChannelMappingRuleDataType) =>
      cn(
        'scroll-mt-32',
        isEditMode &&
          (invalidRowKeys.includes(record.id)
            ? '[&_td]:bg-red-100!'
            : highlightedRowKeys.includes(record.id)
              ? '[&_td]:bg-green-100!'
              : undefined),
      ),
    [highlightedRowKeys, invalidRowKeys, isEditMode],
  );

  return (
    <div ref={tableHostRef} className="px-8 pb-12">
      <DataTable<ChannelMappingRuleDataType>
        key={isEditMode ? 'edit' : 'view'}
        className="**:data-[slot=data-table-sticky-header]:top-22"
        columns={columns}
        data={filteredData}
        emptyMessage={<Trans>No results found. Edit to add new rules.</Trans>}
        error={error}
        loading={isLoading}
        pagination={false}
        getRowId={(row) => row.id}
        tableClassName="[&_td]:align-top"
        initialColumnPinning={{
          start: ['priority'],
          end: isEditMode ? ['deleteButton'] : [],
        }}
        onRow={(record) => ({
          [ROW_SCROLL_DATA_ATTR]: record.id,
          className: rowClassForRecord(record),
        })}
      />
      <ChannelMappingRuleDeleteDialog
        open={!!deleteRow}
        onCancel={() => setDeleteRow(undefined)}
        onDelete={handleDeleteRow}
      />
    </div>
  );
};

export default ChannelMappingRulesTable;
